import { HelpCircle } from "lucide-react";
import { useTranslation } from "react-i18next";

import { ErrorRetryComponent } from "@/components/error-card/error-retry";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { PasswordInput } from "@/components/ui/password-input";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { APP_NAME, useTenantDisplayName } from "@/features/branding";
import { getContractKey, getContractLabelText } from "@/features/legal";
import { cn } from "@/lib/utils";

import { type AuthFormProps } from "./auth-form.types";
import { ContractsLoadingComponent } from "./contracts";

export const AuthForm = (props: AuthFormProps) => {
  const {
    form,
    mode,
    onSubmit,
    isSubmitting,
    hideLoginId,
    contracts,
    isLoadingContracts,
    hasContractsErrored,
    refetchContracts,
  } = props;

  const { t } = useTranslation();
  const clientName = useTenantDisplayName() ?? APP_NAME;

  return (
    <Form {...form}>
      <form
        aria-label={mode === "login" ? "Sign in" : "Register"}
        name="auth-form"
        onSubmit={form.handleSubmit(onSubmit)}
      >
        {!hideLoginId && (
          <FormField
            control={form.control}
            name="loginId"
            render={({ field, fieldState }) => (
              <FormItem>
                <FormLabel className="text-sm font-medium flex items-center gap-2">
                  {t("login_id")}
                  <Popover>
                    <PopoverTrigger asChild>
                      <button type="button" className="inline-flex">
                        <HelpCircle className="h-4 w-4 text-muted-foreground" />
                      </button>
                    </PopoverTrigger>
                    <PopoverContent className="max-w-xs text-sm">
                      {t("login_id_aliases")} {t("login_id_help")}
                    </PopoverContent>
                  </Popover>
                </FormLabel>
                <FormControl>
                  <Input
                    placeholder={t("login_id")}
                    className="h-12"
                    {...field}
                    disabled={isLoadingContracts || field.disabled}
                    error={fieldState.invalid}
                    data-testid={
                      mode === "login"
                        ? "login-id-input"
                        : "onboard-login-id-input"
                    }
                  />
                </FormControl>
                <FormMessage dataTestId="login-id-error" />
              </FormItem>
            )}
          />
        )}
        <div
          className={cn(
            "transition-all duration-500 ease-in-out",
            mode === "register"
              ? "max-h-32 opacity-100 mt-4"
              : "max-h-0 opacity-0 -translate-y-2 pointer-events-none"
          )}
          data-testid="onboard-input-container"
        >
          <FormField
            control={form.control}
            name="yearOfBirth"
            render={({ field, fieldState }) => (
              <FormItem>
                <FormLabel className="text-sm font-medium">
                  {t("auth_accountActivation_dateOfBirth")}
                </FormLabel>
                <FormControl>
                  <Input
                    placeholder={t("auth_accountActivation_dateOfBirth")}
                    className="h-12"
                    {...field}
                    value={field.value || ""}
                    error={fieldState.invalid}
                    tabIndex={mode === "login" ? -1 : undefined}
                    data-testid="onboard-year-of-birth-input"
                  />
                </FormControl>
                <FormMessage dataTestId="year-of-birth-error" />
              </FormItem>
            )}
          />
        </div>

        <FormField
          control={form.control}
          name="pin"
          render={({ field, fieldState }) => (
            <FormItem className="mt-4">
              <FormLabel className="text-sm font-medium">
                {t("auth_pin")}
              </FormLabel>
              <FormControl>
                <PasswordInput
                  placeholder={
                    mode === "login" ? t("enterPin_title") : t("auth_pin")
                  }
                  className="h-12"
                  {...field}
                  error={fieldState.invalid}
                  data-testid={
                    mode === "login" ? "login-pin-input" : "onboard-pin-input"
                  }
                />
              </FormControl>
              <FormMessage dataTestId="pin-error" />
            </FormItem>
          )}
        />

        <div
          className={cn(
            "transition-all duration-500 ease-in-out",
            mode === "register"
              ? "max-h-32 opacity-100 mt-4"
              : "max-h-0 opacity-0 -translate-y-2 pointer-events-none"
          )}
        >
          <FormField
            control={form.control}
            name="confirmPin"
            render={({ field, fieldState }) => (
              <FormItem>
                <FormLabel className="text-sm font-medium">
                  {t("confirmPin_title")}
                </FormLabel>
                <FormControl>
                  <PasswordInput
                    placeholder={t("confirmPin_title")}
                    className="h-12"
                    {...field}
                    value={field.value || ""}
                    error={fieldState.invalid}
                    tabIndex={mode === "login" ? -1 : undefined}
                    data-testid="onboard-confirm-pin-input"
                  />
                </FormControl>
                <FormMessage dataTestId="confirm-pin-error" />
              </FormItem>
            )}
          />
        </div>

        <div
          data-testid="onboarding-contracts"
          className={cn(
            "transition-all duration-500 ease-in-out space-y-1.5",
            mode === "register"
              ? "max-h-40 opacity-100 mt-4"
              : "max-h-0 opacity-0 -translate-y-2 pointer-events-none"
          )}
        >
          {hasContractsErrored ? (
            <ErrorRetryComponent retry={refetchContracts} />
          ) : (
            <>
              {isLoadingContracts ? (
                <ContractsLoadingComponent />
              ) : (
                <>
                  {contracts.map((contract) => {
                    const contractDisplayText = getContractLabelText(
                      contract.title,
                      clientName,
                      t
                    );

                    return (
                      <FormField
                        key={contract.id}
                        control={form.control}
                        name={`contracts.${contract.id}`}
                        render={({ field, fieldState }) => {
                          return (
                            <FormItem>
                              <div
                                className="flex items-center flex-row gap-2"
                                data-testid={`contract-${getContractKey(contract.title)}`}
                              >
                                <FormControl>
                                  <Checkbox
                                    checked={field.value}
                                    onCheckedChange={field.onChange}
                                    error={fieldState.invalid}
                                  />
                                </FormControl>
                                <FormLabel className="text-sm cursor-pointer leading-relaxed">
                                  <span>
                                    {t("accept")}{" "}
                                    {contract.version.document_url ? (
                                      <a
                                        href={contract.version.document_url}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="text-primary hover:underline font-medium"
                                        data-testid={`contract-link-${getContractKey(contract.title)}`}
                                      >
                                        {contractDisplayText}
                                      </a>
                                    ) : (
                                      contractDisplayText
                                    )}
                                    .
                                  </span>
                                </FormLabel>
                              </div>

                              <FormMessage
                                data-testid={`contract-${getContractKey(contract.title)}-error-message`}
                              />
                            </FormItem>
                          );
                        }}
                      />
                    );
                  })}
                </>
              )}
            </>
          )}
        </div>

        <Button
          className="w-full mt-6"
          type="submit"
          size="lg"
          loading={isSubmitting}
          disabled={isLoadingContracts || hasContractsErrored}
          data-testid={`${mode}-button`}
        >
          {mode === "login"
            ? t("signIn")
            : t("auth_create_account_and_sign_in")}
        </Button>
      </form>
    </Form>
  );
};
